import { useI18n } from '../i18n/useI18n';
import { Alert } from './Alert';
import { CopyField } from './CopyField';

/**
 * The address a tenancy's identity provider has to return a browser to.
 *
 * It is a setting on somebody else's system, and the one step of a sign-in that
 * nothing here can do or check: the control plane cannot register this address
 * at the tenancy's provider, and it cannot ask whether it was registered. An
 * address that was not registered is refused by the provider before the request
 * reaches us at all, so it never appears in any log of ours — the person sees
 * `redirect_uri_mismatch` and has nothing to compare it against.
 *
 * That is why this is a block with the string in full and a button, in both the
 * places the server sends it, rather than a line of small print in one of them.
 *
 * Shown wherever the answer carries it; when it carries an empty one the block
 * says so instead of drawing an empty box to copy.
 */
export function RedirectUriNotice({ value }: { value: string }) {
  const { t } = useI18n();

  return (
    <Alert tone="info" title={t('redirectUri.title')}>
      <p>{t('redirectUri.body')}</p>

      {value === '' ? (
        <p style={{ marginTop: 10 }}>{t('redirectUri.unnamed')}</p>
      ) : (
        <div style={{ marginTop: 12 }}>
          <CopyField label={t('redirectUri.label')} value={value} />
        </div>
      )}

      {/* True of every tenancy for ever, and needed once — by whoever is about
          to change provider and wonders whether to register a new address. */}
    </Alert>
  );
}
